<template>
  <div
    class="flex flex-col items-center mx-4 mt-3 px-2"
    id="small-receipt"
    :class="{
      'dir-ltr': $ctx.receiptLanguage == 'en',
      'dir-rtl': $ctx.receiptLanguage == 'ar'
    }"
  >
    <div class="flex flex-col items-center w-full" style="height: 100vh;">
      <!-- header info -->
      <div id="small-receipt-header" class="flex flex-col w-full">
        <div class="flex mt-6 justify-center w-full">
          <img
            :src="settingsStore.getValue('receipt_logo') ? settingsStore.storagePath + '/' + settingsStore.getValue('receipt_logo'): '/images/logo.svg'"
            :width="settingsStore.getValue('receipt_logo_width')"
            alt=""
          />
        </div>
      </div>

      <div id="small-receipt-body" class="w-full">
        <!-- title -->
        <div class="flex flex-col w-full mt-2 items-center">
          <h2 class="bg-gray-200 rounded py-1 px-2 font-bold mt-3 capitalize">
            <p>{{ receiptTranslate("Inventory Receiving Receipt", "receipt.inventoryReceivingReceipt") }}</p>
          </h2>
        </div>

        <div class="flex flex-col px-5 items-center">
          <div>
            <svg :id="`barcode-${inventoryReceiving?.externalID}`" class="mt-3"></svg>
            <div class="flex items-center justify-center">
              <span class="font-bold mx-5" style="font-size: 20px;">
                {{ inventoryReceiving?.externalID?.toString().slice(0, 5) }}-{{ inventoryReceiving?.externalID?.toString().slice(5) }}
              </span>
            </div>
          </div>
        </div>

        <div class="mx-5 mt-5 pb-2 text-sm self-end border-b border-black">
          <p style="font-size: 12px;">{{ settingsStore.getValue('company_address') }}</p>
          <p :class="{'ltr-numbers': settingsStore.getValue('receipt_language') == 'ar'}">
            {{ settingsStore.getValue('company_phone1') + (settingsStore.getValue('company_phone2')? ` - ${settingsStore.getValue('company_phone2')}`: '') }}
          </p>
          <p>{{ settingsStore.getValue('company_email') }}</p>
          <p>{{ settingsStore.getValue('company_website') }}</p>
        </div>

        <div class="flex w-full px-5 mt-4">
          <div>
            <table class="w-full text-sm">
              <tr>
                <td><p>{{ receiptTranslate("Vendor", "receipt.vendor") }}</p></td>
                <td><p class="font-bold mx-5">{{ inventoryReceiving?.vendor?.name || inventoryReceiving?.vendorName }}</p></td>
              </tr>
              <tr v-if="inventoryReceiving?.user">
                <td><p>{{ receiptTranslate("Received By", "receipt.receivedBy") }}</p></td>
                <td><p class="font-bold mx-5">{{ inventoryReceiving?.user?.fullName }}</p></td>
              </tr>
              <tr v-if="inventoryReceiving?.receivedOn">
                <td><p>{{ receiptTranslate("Received On", "receipt.receivedOn") }}</p></td>
                <td><p class="font-bold mx-5" dir="ltr">{{ formatPostgresDate(inventoryReceiving.receivedOn, 'YYYY-MM-DD') }}</p></td>
              </tr>
              <tr v-if="inventoryReceiving?.location">
                <td><p>{{ receiptTranslate("Location", "receipt.location") }}</p></td>
                <td><p class="font-bold mx-5">{{ $ctx.locale === 'ar' ? inventoryReceiving?.location?.arName : inventoryReceiving?.location?.enName }}</p></td>
              </tr>
            </table>
          </div>
        </div>

        <!-- received items table -->
        <div class="text-sm w-full mt-4 px-5">
          <table class="w-full" :class="{ 'ar-receipt': $ctx.receiptLanguage == 'ar' }">
            <thead>
              <tr class="border-b-2">
                <th class="text-left">{{ receiptTranslate("Item", "receipt.item") }}</th>
                <th class="text-center">{{ receiptTranslate("Qty", "receipt.qty") }}</th>
                <th class="text-right">{{ receiptTranslate("Cost", "receipt.cost") }}</th>
                <th class="text-right">{{ receiptTranslate("Total", "receipt.total") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(iri, index) in inventoryReceiving?.inventoryReceivingItems" :key="iri.id">
                <td class="text-left">{{ iri.item?.name || iri.item?.displayName }}</td>
                <td class="text-center">{{ iri.quantity }}</td>
                <td class="text-right">{{ formatDinero(iri.costDinero).replace("IQD", "") }}</td>
                <td class="text-right">{{ formatDinero(iri.costDinero.multiply(iri.quantity)).replace("IQD", "") }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- financial summary -->
        <div class="text-sm flex flex-col w-full px-5 mt-4">
          <div class="w-full">
            <div class="w-full border-t border-gray-300 pt-2">
              <table class="payment-table w-full" :class="{ 'ar-receipt': $ctx.receiptLanguage == 'ar' }">
                <tr>
                  <td>{{ receiptTranslate("Total (before extras)", "receipt.totalBeforeExtras") }}</td>
                  <td><p class="text-right">{{ formatDinero(inventoryReceiving?.totalCostDinero) }}</p></td>
                </tr>
                <tr>
                  <td>{{ receiptTranslate("Other Costs", "receipt.otherCosts") }}</td>
                  <td><p class="text-right">{{ formatDinero(inventoryReceiving?.vendorBill?.otherCostsDinero || inventoryReceiving?.totalCostDinero?.multiply(0)) }}</p></td>
                </tr>
                <tr>
                  <td>{{ receiptTranslate("Total", "receipt.total") }}</td>
                  <td><p class="text-right">{{ formatDinero(grandTotal) }}</p></td>
                </tr>
                <tr>
                  <td>{{ receiptTranslate("Money Owed", "receipt.moneyOwed") }}</td>
                  <td><p class="text-right">{{ formatDinero(inventoryReceiving?.vendorBill?.totalDueDinero || grandTotal) }}</p></td>
                </tr>
                <tr>
                  <td>{{ receiptTranslate("Payments Made", "receipt.paymentsMade") }}</td>
                  <td><p class="text-right">{{ formatDinero(inventoryReceiving?.vendorBill?.totalPaidDinero || inventoryReceiving?.totalCostDinero?.multiply(0)) }}</p></td>
                </tr>
                <tr>
                  <td>{{ receiptTranslate("Remaining Balance", "receipt.remainingBalance") }}</td>
                  <td><p class="text-right font-bold">{{ formatDinero(inventoryReceiving?.vendorBill?.totalBalanceDinero || grandTotal) }}</p></td>
                </tr>
                <tr>
                  <td>{{ receiptTranslate("Total Items", "receipt.totalItems") }}</td>
                  <td><p class="text-right font-bold">{{ totalItemsCount }}</p></td>
                </tr>
              </table>
            </div>
          </div>
        </div>

        <!-- notes -->
        <div v-if="inventoryReceiving?.notes" class="px-5 mt-4">
          <p class="font-bold">{{ receiptTranslate("Notes", "receipt.notes") }}:</p>
          <p class="text-sm">{{ inventoryReceiving?.notes }}</p>
        </div>

        <!-- transactions list (payments) -->
        <div v-if="transactions.length > 0" class="mt-4 px-5">
          <div class="flex flex-col w-full">
            <h3 class="bg-gray-100 rounded py-1 px-2 font-bold text-center">
              {{ receiptTranslate("Payment Transactions", "receipt.paymentTransactions") }}
            </h3>
          </div>

          <div class="text-sm flex flex-col w-full mt-2">
            <div class="w-full" v-for="transaction in transactions" :key="transaction.id">
              <div class="border-t border-gray-300 pt-2 mt-2">
                <table class="payment-table w-full" :class="{ 'ar-receipt': $ctx.receiptLanguage == 'ar' }">
                  <tr>
                    <td colspan="2">
                      <p class="font-bold">{{ receiptTranslate("Payment ID", "receipt.paymentID") }}: {{ transaction.externalID }}</p>
                    </td>
                  </tr>
                  <tr>
                    <td>{{ receiptTranslate("Date", "receipt.date") }}</td>
                    <td><p class="text-right">{{ parseFormat(transaction.createdAt) }}</p></td>
                  </tr>
                  <tr>
                    <td>{{ receiptTranslate("Amount", "receipt.amount") }}</td>
                    <td><p class="text-right">{{ formatDinero(transaction.amountDinero) }}</p></td>
                  </tr>
                  <tr v-if="transaction.description">
                    <td>{{ receiptTranslate("Notes", "receipt.notes") }}</td>
                    <td><p class="text-right">{{ transaction.description }}</p></td>
                  </tr>
                  <tr v-if="transaction.user && settingsStore.getValue('hide_cashier_name_in_receipt') != 'true'">
                    <td>{{ receiptTranslate("Cashier", "receipt.cashier") }}</td>
                    <td><p class="text-right">{{ transaction.user.fullName }}</p></td>
                  </tr>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        id="small-receipt-footer"
        v-if="settingsStore.getValue('show_receipts_thecky_footer') == 'true'"
        class="flex justify-center self-center w-full mt-auto"
      >
        <div class="flex items-center justify-between py-2 px-4 mt-2 mb-4" style="bottom: 0px; width: 95%; border-top: 2px solid #BDC7D6">
          <p><img src="@/assets/images/thecky-qr.png" style="height: 25px;"/></p>
          <p><img src="@/assets/images/footer.svg" style="height: 25px;"/></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { formatPostgresDate, parseFormat } from "@/utils/date";
import { useI18n } from "vue-i18n";
import { formatDinero } from "@/utils/money";
import { Transaction } from "@/models/accounting/Transaction";
import JsBarcode from "jsbarcode";
import { useSettingsStore } from "@/stores/settingsStore";
import InventoryReceiving from "@/models/inventory/InventoryReceiving";

export default defineComponent({
  components: {},

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          receipt: {
            inventoryReceivingReceipt: 'Inventory Receiving Receipt',
            vendor: 'Vendor',
            receivedBy: 'Received By',
            receivedOn: 'Received On',
            location: 'Location',
            notes: 'Notes',
            receivedItems: 'Received Items',
            item: 'Item',
            qty: 'Qty',
            cost: 'Cost',
            total: 'Total',
            otherCosts: 'Other Costs',
            grandTotal: 'Grand Total',
            totalItems: 'Total Items',
            paymentTransactions: 'Payment Transactions',
            paymentID: 'Payment ID',
            date: 'Date',
            amount: 'Amount',
            cashier: 'Cashier',
            totalBeforeExtras: 'Total (before extras)',
            moneyOwed: 'Money Owed',
            paymentsMade: 'Payments Made',
            remainingBalance: 'Remaining Balance'
          }
        },
        ar: {
          receipt: {
            inventoryReceivingReceipt: 'ايصال استلام مخزون',
            vendor: 'المورد',
            receivedBy: 'استلم من قبل',
            receivedOn: 'تاريخ الاستلام',
            location: 'الموقع',
            notes: 'ملاحظات',
            receivedItems: 'المواد المستلمة',
            item: 'المادة',
            qty: 'الكمية',
            cost: 'التكلفة',
            total: 'المجموع',
            otherCosts: 'تكاليف اخرى',
            grandTotal: 'المجموع الكلي',
            totalItems: 'عدد المواد',
            paymentTransactions: 'حركات الدفع',
            paymentID: 'رقم الدفعة',
            date: 'التاريخ',
            amount: 'المبلغ',
            cashier: 'الكاشير',
            totalBeforeExtras: 'المجموع (قبل التكاليف الإضافية)',
            moneyOwed: 'المبلغ المطلوب',
            paymentsMade: 'المبلغ المدفوع',
            remainingBalance: 'المبلغ المتبقي'
          }
        }
      }
    });

    const settingsStore = useSettingsStore();
    return { t, settingsStore, formatPostgresDate };
  },

  props: {
    inventoryReceiving: {
      type: Object as PropType<InventoryReceiving>,
      required: true
    },
    transactions: {
      type: Array as PropType<Array<Transaction>>,
      required: true
    }
  },

  computed: {
    totalItemsCount() {
      if (!this.inventoryReceiving?.inventoryReceivingItems) return 0;
      return this.inventoryReceiving.inventoryReceivingItems.reduce((acc, item) => acc + (item.quantity || 0), 0);
    },
    grandTotal() {
      const total = this.inventoryReceiving?.totalCostDinero;
      const otherCosts = this.inventoryReceiving?.vendorBill?.otherCostsDinero;
      if (total && otherCosts) {
        return total.add(otherCosts);
      }
      return total;
    }
  },

  mounted() {
    setTimeout(() => {
      if (this.inventoryReceiving?.externalID) {
        JsBarcode(
          `#barcode-${this.inventoryReceiving.externalID}`,
          String(this.inventoryReceiving.externalID),
          {
            height: 50,
            displayValue: false
          }
        );
      }
    }, 100);
  },

  created() {
    this.addPrintCSS();
  },

  beforeUnmount() {
    this.removePrintCSS();
  },

  methods: {
    parseFormat,
    formatDinero,
    addPrintCSS() {
      const style = document.createElement("style");
      style.setAttribute("id", "print-css-small");
      style.textContent = `
        @media print {
          @page {
            size: 80mm auto;
            margin: 0;
          }
        }`;
      document.head.appendChild(style);
    },
    removePrintCSS() {
      const style = document.querySelector("#print-css-small");
      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }
    },
    receiptTranslate(value: string, translateKey: string): string {
      return this.$ctx.receiptLanguage == "en" ? value : this.t(translateKey, this.$ctx.receiptLanguage);
    }
  }
});
</script>

<style>
.thecky-site {
  color: #000;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 3px;
}

#small-receipt {
  width: 80mm;
  overflow: scroll;
  background-color: #fff;
}

.payment-table {
  width: 100%;
  border-collapse: collapse;
}

.payment-table td {
  padding: 4px;
}
@media print {
  body * {
    visibility: hidden;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  #small-receipt,
  #small-receipt * {
    visibility: visible;
  }

  #small-receipt {
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: auto;
    overflow: visible;
    margin: 0;
  }
}
</style>
